import React, { useState, useMemo } from 'react';
import Pagination from 'react-js-pagination';
import { useSelector, useDispatch } from 'react-redux';
import { resetSearch } from '@store/reducers/search';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { Table, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { createUrlWithParam } from '../../utils/helper';
import { URI_INTERNAL } from '../../utils/pathLocations';
import { objectShowNumber, messageToast } from '../../utils/define';
import { ConfirmModal } from '../../modules/modal/ConfirmModal';
import { Get, DeleteSingle } from '../../utils/httpProvider';
import { JOBGROUP_API } from '../../utils/apiUrl';
import ReactSpinner from '../../components/reactSpinner/ReactSpinner';

const JobGroupList = () => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const valueSearch = useSelector((state) => state.search.valueSearch);

    const [isLoading, setIsLoading] = useState(false);
    const [listJobGroup, setListJobGroup] = useState([]);
    const [totalJobGroup, setTotalJobGroup] = useState(0);

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    const handleSort = (field) => {
        if (sortField === field) {
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else if (sortDirection === 'desc') {
                setSortField(null);
                setSortDirection(null);
            } else {
                setSortDirection('asc');
            }
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const renderSortIcon = (field) => {
        if (sortField !== field) {
            return <i className="fas fa-sort text-muted ms-1" style={{ fontSize: '0.8rem' }}></i>;
        }
        return sortDirection === 'asc' 
            ? <i className="fas fa-sort-up text-primary ms-1" style={{ fontSize: '0.8rem' }}></i>
            : <i className="fas fa-sort-down text-primary ms-1" style={{ fontSize: '0.8rem' }}></i>;
    };

    const sortedJobGroups = useMemo(() => {
        if (!sortField || !sortDirection) return listJobGroup;
        return [...listJobGroup].sort((a, b) => {
            let valA = a[sortField];
            let valB = b[sortField];

            if (typeof valA === 'string' && typeof valB === 'string') {
                return sortDirection === 'asc'
                    ? valA.localeCompare(valB, 'vi', { sensitivity: 'base' })
                    : valB.localeCompare(valA, 'vi', { sensitivity: 'base' });
            }

            if (valA === null || valA === undefined) valA = 0;
            if (valB === null || valB === undefined) valB = 0;
            return sortDirection === 'asc' ? valA - valB : valB - valA;
        });
    }, [listJobGroup, sortField, sortDirection]);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [search, setSearch] = useState('');

    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');

    React.useEffect(() => {
        const paramUrl = new URLSearchParams(location.search);
        const pPage = paramUrl.get('page');
        const pSize = paramUrl.get('size');
        const pSearch = paramUrl.get('search');

        if (pPage != null && parseInt(pPage, 10) !== page) {
            setPage(parseInt(pPage, 10));
        }
        if (pSize != null && parseInt(pSize, 10) !== pageSize) {
            setPageSize(parseInt(pSize, 10));
        }
        if (pSearch != null && pSearch !== search) {
            setSearch(pSearch);
        }
    }, [location.search]);

    const fetchPagingData = async (Page, Size, Search) => {
        setIsLoading(true);
        const filter = {
            Keyword: Search || '',
            PageIndex: Page,
            PageSize: Size
        };
        try {
            const response = await Get(JOBGROUP_API.GET_PAGING, null, filter);
            if (response && response.data) {
                setListJobGroup(response.data);
                setTotalJobGroup(response.totalRecords || 0);
            }
        } catch (error) {
            toast.error('Lỗi khi lấy danh sách nhóm công việc!');
        } finally {
            setIsLoading(false);
        }
    };

    const replaceHistory = async (Page, Size, Search) => {
        await fetchPagingData(Page, Size, Search);
        history.replace(
            createUrlWithParam(URI_INTERNAL.JOBGROUP_PATH, {
                page: Page,
                size: Size,
                search: Search
            })
        );
    };

    React.useEffect(() => {
        if (valueSearch != null && valueSearch !== search) {
            setSearch(valueSearch);
        }
    }, [valueSearch]);

    React.useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            replaceHistory(page, pageSize, search);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [page, pageSize, search]);

    // Reset search khi rời trang
    React.useEffect(() => {
        return () => { dispatch(resetSearch()); };
    }, []);

    const changePage = (number) => {
        setPage(number);
    };

    const onChangeShowNumber = (e) => {
        setPageSize(parseInt(e.target.value, 10));
        setPage(1);
    };

    const deleteAction = (id, name) => {
        setIdWantDelete(id);
        setConfirmDelMessage(name);
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = async () => {
        if (idWantDelete !== '') {
            setIsLoading(true);
            try {
                const result = await DeleteSingle(JOBGROUP_API.DELETE, idWantDelete, null);
                if (result.status === 200) {
                    toast.success(messageToast.messageToastDeleteSuccess);
                    fetchPagingData(page, pageSize, search);
                } else {
                    toast.error(messageToast.messageToastDeleteError);
                }
            } catch (error) {
                toast.error(messageToast.messageToastTryCatch);
            } finally {
                setIsLoading(false);
                setOpenConfirmDeleteModal(false);
            }
        }
    };

    const cancelDelete = () => {
        setIdWantDelete('');
        setOpenConfirmDeleteModal(false);
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">
                                Danh mục Nhóm công việc
                            </h3>
                        </div>
                        <div className="card-body">
                            <div className="d-flex justify-content-between mb-3">
                                <form className="form-inline">
                                    <label className="mr-2">
                                        Hiển thị :
                                    </label>
                                    <select
                                        className="form-control mr-3"
                                        value={pageSize}
                                        onChange={(e) => onChangeShowNumber(e)}
                                    >
                                        {objectShowNumber.map((item, key) => (
                                            <option
                                                key={Number(key)}
                                                value={item.value}
                                            >
                                                {item.text}
                                            </option>
                                        ))}
                                    </select>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Nhập tên công việc để tìm kiếm"
                                        value={search}
                                        onChange={(e) => {
                                            setSearch(e.target.value);
                                            setPage(1);
                                        }}
                                    />
                                </form>
                                <NavLink to={URI_INTERNAL.JOBGROUP_PATH_ADD}>
                                    <Button variant="info">
                                        <i className="fas fa-plus"></i> Thêm mới
                                    </Button>
                                </NavLink>
                            </div>
                            <div className="dataTables_wrapper dt-bootstrap4">
                                <div className="row">
                                    <div className="col-sm-12">
                                        <Table striped bordered hover responsive>
                                            <thead>
                                                <tr role="row">
                                                    <th>STT</th>
                                                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>
                                                        Tên nhóm công việc {renderSortIcon('name')}
                                                    </th>
                                                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('description')}>
                                                        Mô tả {renderSortIcon('description')}
                                                    </th>
                                                    <th>Thao tác</th>
                                                </tr>
                                            </thead>
                                            {sortedJobGroups != null &&
                                            sortedJobGroups.length > 0 ? (
                                                <tbody>
                                                    {sortedJobGroups.map(
                                                        (item, index) => (
                                                            <tr
                                                                key={item.id}
                                                                className="odd"
                                                            >
                                                                <td>
                                                                    {index +
                                                                        1 +
                                                                        (page - 1) * pageSize}
                                                                </td>
                                                                <td className="font-weight-bold">{item.name}</td>
                                                                <td>{item.description}</td>
                                                                <td className="text-center">
                                                                    <Button
                                                                        variant="link"
                                                                        className="text-primary p-0 mr-3"
                                                                        title="Sửa"
                                                                        onClick={() => history.push(createUrlWithParam(URI_INTERNAL.JOBGROUP_PATH_EDIT, { id: item.id }))}
                                                                    >
                                                                        <i className="fas fa-edit fs-5"></i>
                                                                    </Button>
                                                                    <Button
                                                                        variant="link"
                                                                        className="text-danger p-0"
                                                                        title="Xóa"
                                                                        onClick={() => deleteAction(item.id, item.name)}
                                                                    >
                                                                        <i className="fas fa-trash fs-5"></i>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        )
                                                    )}
                                                </tbody>
                                            ) : (
                                                <tbody>
                                                    <tr className="text-center">
                                                        <td colSpan="4">
                                                            Không có dữ liệu
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            )}
                                        </Table>
                                    </div>
                                </div>
                                {totalJobGroup > pageSize && (
                                    <div className="row">
                                        <div className="col-sm-12">
                                            <div className="dataTables_paginate paging_simple_numbers">
                                                <Pagination
                                                    itemClass="page-item"
                                                    linkClass="page-link"
                                                    innerClass="pagination pagination-sm no-margin pull-right"
                                                    activePage={page}
                                                    itemsCountPerPage={pageSize}
                                                    totalItemsCount={totalJobGroup}
                                                    pageRangeDisplayed={5}
                                                    onChange={(number) => changePage(number)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <ConfirmModal
                                isOpen={openConfirmDeleteModal}
                                confirmType="delete"
                                handleAccept={() => acceptDelete()}
                                handleCancel={() => cancelDelete()}
                                content={confirmDelMessage}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <ReactSpinner loadings={isLoading} />
        </div>
    );
};

export default JobGroupList;
